import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Eager load critical components
import DashboardSidebar from './components/DashboardSidebar';
import UnifiedHeader from '../../components/unified/UnifiedHeader';
import { LazyLoadBoundary, ComponentLoader, AnalyticsChartSkeleton, DashboardCardSkeleton } from '../../components/LazyLoadBoundary';
const CareerInsights = lazy(() => import('./components/CareerInsights'));

// Lazy load heavy dashboard components with smart chunking
const DashboardOverview = lazy(() => import('./components/DashboardOverview'));
const Jobs = lazy(() => import('./components/Jobs'));
const Interviews = lazy(() => import('./components/Interviews'));
const Inbox = lazy(() => import('./components/Inbox'));
const Profile = lazy(() => import('./components/Profile'));
const CompanySwitcher = lazy(() => import('./components/CompanySwitcher'));
const AccountSettings = lazy(() => import('./components/AccountSettings'));
const Applications = lazy(() => import('./components/Applications'));
const SavedJobs = lazy(() => import('./components/SavedJobs'));
const Messages = lazy(() => import('./components/Messages'));
const Notifications = lazy(() => import('./components/Notifications'));



const JobSeekerDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const location = useLocation();

  useEffect(() => {
    document.title = 'Dashboard - Recrutees';
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Layout animation variants - enhanced spring animation
  const contentVariants = {
    expanded: {
      marginLeft: sidebarCollapsed ? 64 : 240,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 35,
        mass: 0.8,
      }
    }
  };

  // Page transition variants for smoother content switching
  const pageVariants = {
    initial: { 
      opacity: 0, 
      x: 20,
      scale: 0.98
    },
    enter: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.4
      }
    },
    exit: { 
      opacity: 0, 
      x: -20,
      scale: 0.98,
      transition: {
        duration: 0.2
      }
    }
  };

  // Mock data for header
  const mockNotifications = [
    { id: 1, title: 'Application viewed', message: 'Google reviewed your application for Software Engineer', time: '2 min ago', unread: true },
    { id: 2, title: 'New job match', message: 'Senior React Developer at Microsoft', time: '1 hour ago', unread: true },
    { id: 3, title: 'Interview scheduled', message: 'Technical interview with Netflix on Friday', time: '3 hours ago', unread: false }
  ];

  const mockMessages = [
    { id: 1, from: 'Sarah Chen', company: 'Google', message: 'Thanks for your application. We\'d like to schedule...', time: '5 min ago', avatar: 'SC' },
    { id: 2, from: 'Mike Johnson', company: 'Microsoft', message: 'Can you tell me more about your React experience?', time: '2 hours ago', avatar: 'MJ' },
    { id: 3, from: 'Lisa Park', company: 'Netflix', message: 'Congratulations! We\'d like to move forward...', time: '1 day ago', avatar: 'LP' }
  ];

  const mockUser = {
    name: 'John Doe',
    email: 'john.doe@example.com',
    initials: 'JD'
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <LazyLoadBoundary fallback={<DashboardCardSkeleton />} errorFallback={<div>Error loading overview</div>} onError={(error) => console.error(error)}><DashboardOverview /></LazyLoadBoundary>;
      case 'profile':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading profile</div>} onError={(error) => console.error(error)}><Profile /></LazyLoadBoundary>;
      case 'jobs':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading jobs</div>} onError={(error) => console.error(error)}><Jobs /></LazyLoadBoundary>;
      case 'applications':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading applications</div>} onError={(error) => console.error(error)}><Applications /></LazyLoadBoundary>;
      case 'saved':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading saved jobs</div>} onError={(error) => console.error(error)}><SavedJobs /></LazyLoadBoundary>;
      case 'messages':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading messages</div>} onError={(error) => console.error(error)}><Messages /></LazyLoadBoundary>;
      case 'notifications':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading notifications</div>} onError={(error) => console.error(error)}><Notifications /></LazyLoadBoundary>;
      case 'interviews':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading interviews</div>} onError={(error) => console.error(error)}><Interviews /></LazyLoadBoundary>;
      case 'insights':
        return <LazyLoadBoundary fallback={<AnalyticsChartSkeleton />} errorFallback={<div>Error loading insights</div>} onError={(error) => console.error(error)}><CareerInsights /></LazyLoadBoundary>;
      case 'account':
        return <LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading account settings</div>} onError={(error) => console.error(error)}><AccountSettings /></LazyLoadBoundary>;
      default:
        return <LazyLoadBoundary fallback={<DashboardCardSkeleton />} errorFallback={<div>Error loading overview</div>} onError={(error) => console.error(error)}><DashboardOverview /></LazyLoadBoundary>;
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Header */}
      <UnifiedHeader 
        variant="dashboard"
        onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
        sidebarCollapsed={sidebarCollapsed}
        user={mockUser}
        notifications={mockNotifications}
        messages={mockMessages}
      />
      {/* Sidebar with enhanced animation */}
      <DashboardSidebar 
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      {/* Main Content with responsive layout animation */}
      <motion.main
        className="flex-1 overflow-auto"
        variants={contentVariants}
        animate="expanded"
        style={{ 
          marginLeft: sidebarCollapsed ? 0 : 0,
          paddingTop: 64 // Account for fixed header height
        }}
      >
        {/* Breadcrumb Navigation */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border" style={{ marginLeft: sidebarCollapsed ? 64 : 240 }}>
          <div className="px-4 md:px-6 py-3">
            <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
              <a href="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
                Dashboard
              </a>
              {location?.pathname !== '/dashboard' && (
                <>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-foreground font-medium capitalize">
                    {location?.pathname?.split('/')?.[2] || 'Overview'}
                  </span>
                </>
              )}
            </nav>
          </div>
        </div>
        
        <div className="p-4 md:p-6" style={{ marginLeft: sidebarCollapsed ? 64 : 240 }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location?.pathname}
              variants={pageVariants}
              initial="initial"
              animate="enter"
              exit="exit"
            >
              <Routes location={location}>
                {/* Default home section - Dashboard Overview */}
                <Route index element={<LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading overview</div>} onError={(error) => console.error(error)}><DashboardOverview /></LazyLoadBoundary>} />
                <Route path="home" element={<Navigate to="/dashboard" replace />} />
                
                {/* Other dashboard sections */}
                <Route path="jobs" element={<LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading jobs</div>} onError={(error) => console.error(error)}><Jobs /></LazyLoadBoundary>} />
                <Route path="interviews" element={<LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading interviews</div>} onError={(error) => console.error(error)}><Interviews /></LazyLoadBoundary>} />
                <Route path="inbox" element={<LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading inbox</div>} onError={(error) => console.error(error)}><Inbox /></LazyLoadBoundary>} />
                <Route path="profile" element={<LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading profile</div>} onError={(error) => console.error(error)}><Profile /></LazyLoadBoundary>} />
                <Route path="insights" element={<LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading insights</div>} onError={(error) => console.error(error)}><CareerInsights /></LazyLoadBoundary>} />
                <Route path="switch" element={<LazyLoadBoundary fallback={<ComponentLoader />} errorFallback={<div>Error loading switcher</div>} onError={(error) => console.error(error)}><CompanySwitcher /></LazyLoadBoundary>} />
                
                {/* Legacy routes for backward compatibility - all redirect to main sections */}
                <Route path="jobs/recommended" element={<Navigate to="/dashboard/jobs" replace />} />
                <Route path="jobs/saved" element={<Navigate to="/dashboard/jobs" replace />} />
                <Route path="jobs/applications" element={<Navigate to="/dashboard/jobs" replace />} />
                <Route path="messages" element={<Navigate to="/dashboard/inbox" replace />} />
                <Route path="notifications" element={<Navigate to="/dashboard/inbox" replace />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.main>
    </div>
  );
};

export default JobSeekerDashboard;